import React, { memo } from 'react';

import { Box, Divider, IconButton, Typography } from '@mui/material';
import { DeleteIcon, EditPenIcon } from '@sharedCR/coreDesignSystem/icons';
import InfoTitle from '@sharedCR/coreDesignSystem/InfoTitle';
import { checkHasVisibleFields } from 'app/pages/system/autoLockRules/AutoLockRules.utils';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { FormGeneratorTestIds } from '../formGenerator.testIds';

import { getGroupStyles } from './FormGeneratorGroup.styles';

const defaultArray = [];
const defaultObject = {};

const FormGeneratorGroup = ({
  isLoading,
  numberOfColumns,
  fields = defaultArray,
  title,
  groupTitle,
  index,
  isInModal,
  dataTestId,
  onGroupClick,
  onGroupDelete,
  methods,
  customStyles,
  additionalGroupContentConfig,
  renderFormItem,
  renderControls,
  customControls,
  ignoreEmptyGroups = false,
  hiddenFieldsMap = defaultObject,
}) => {
  const isFieldsExist = !!fields.length;
  const hasVisibleFields = checkHasVisibleFields(fields, hiddenFieldsMap);

  if (ignoreEmptyGroups && !isFieldsExist) {
    return null;
  }

  return (
    <Box
      className={clsx('w-full', title?.className)}
      data-group-index={index}
      data-is-group="true"
      data-is-modal={isInModal}
      data-testid={dataTestId}
      onClick={onGroupClick}
    >
      {groupTitle && hasVisibleFields && (
        <InfoTitle
          className={groupTitle.className ?? 'my-24'}
          icon={groupTitle.icon}
          title={groupTitle.label}
        />
      )}
      {!!title && (
        <Box>
          {title.withDivider && <Divider sx={title.dividerStyles} />}

          <Box
            className="flex items-center justify-between my-4 title-wrapper"
            sx={title.wrapperStyles}
          >
            <Box className="inline-flex items-center title-container" sx={title.containerStyles}>
              <Typography
                className="title"
                component="h5"
                sx={title.titleStyles}
                variant="medium16"
              >
                {!!title.icon && (
                  <Box sx={title.iconWrapperStyles}>
                    <Box sx={title.iconStyles}>
                      <title.icon />
                    </Box>
                  </Box>
                )}
                {title.label}
              </Typography>
            </Box>

            {!!onGroupDelete && (
              <IconButton
                data-group-index={index}
                data-testid={FormGeneratorTestIds.GROUP_DELETE_BUTTON}
                disabled={isLoading}
                onClick={onGroupDelete}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
        </Box>
      )}

      <Box sx={getGroupStyles({ numberOfColumns, customStyles })}>
        {fields.map(renderFormItem)}

        {renderControls?.({ methods, index, isLoading }) ?? (
          <>
            {!!methods?.onGroupEdit && (
              <IconButton
                className="mb-24"
                data-group-index={index}
                data-testid={FormGeneratorTestIds.GROUP_EDIT_CONTROL_BUTTON}
                disabled={isLoading}
                onClick={methods.onGroupEdit}
              >
                <EditPenIcon />
              </IconButton>
            )}

            {!!methods?.onGroupDelete && (
              <IconButton
                className="mb-24"
                data-group-index={index}
                data-testid={FormGeneratorTestIds.GROUP_DELETE_CONTROL_BUTTON}
                disabled={isLoading}
                onClick={methods.onGroupDelete}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </>
        )}

        {customControls}

        {!!additionalGroupContentConfig && (
          <Box
            className={additionalGroupContentConfig.className}
            sx={additionalGroupContentConfig.wrapperStyles}
          >
            {additionalGroupContentConfig.fields?.map(
              ({ key, component: Component, disabled, ...restProps }, i) => (
                <Component
                  key={key ?? i}
                  {...restProps}
                  data-group-index={index}
                  disabled={isLoading || disabled}
                />
              ),
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

FormGeneratorGroup.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  numberOfColumns: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
  isInModal: PropTypes.bool.isRequired,
  renderFormItem: PropTypes.func.isRequired,
  renderControls: PropTypes.func,
  fields: PropTypes.array,
  title: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  ignoreEmptyGroups: PropTypes.bool,
  customControls: PropTypes.node,
  additionalGroupContentConfig: PropTypes.object,
  customStyles: PropTypes.object,
  methods: PropTypes.object,
  dataTestId: PropTypes.string,
  groupTitle: PropTypes.object,
  hiddenFieldsMap: PropTypes.object,
  onGroupClick: PropTypes.func,
  onGroupDelete: PropTypes.func,
};

export default memo(FormGeneratorGroup);
